/*
* Pipeline Theme — assets/custom.js (Tom's Studio)
*
* Loaded deferred from layout/theme.liquid on every page. Five things, each
* self-contained and cheap on pages that don't use it:
*
*   1. Sale guard   — slider product pages only: if the product form's variant
*                     id disagrees with the picker after a swap, take Add to
*                     Cart out of service until the next swatch click.
*   2. Variant change — the original file's handler: show/hide the legacy
*                     Klaviyo trigger, and on Colour Block pages keep each
*                     sibling-product link on the matching colour combination.
*   3. Type-to-jump — the country / language popouts in header and footer.
*   4. Countdown    — the PDP countdown snippet (cb-pdp-countdown-timer).
*   5. Test price   — Intelligems price for the Add to Cart button total.
*
* Removed Oct 2026: the Klaviyo back-in-stock button juggling (document-wide
* MutationObserver, button text scanning, Notify-button re-attachment and the
* buy-button fade). Klaviyo's own form now does whatever it does unaided.
*/
(function () {
  'use strict';

  // =====================================================================
  // 1. Sale guard (slider PDPs only).
  //
  // theme.js has a race we don't own: two option changes inside ~500 ms can
  // leave the product form's [name="id"] on a stale variant while every
  // visible control shows the new one, so Add to Cart would add the wrong pen.
  // We never repair it (that would fight theme.js for the same swap); we only
  // disable the button, and the next swatch click re-runs the swap and this
  // check. The combination map is #cb_combinations_custom_slider, rendered by
  // snippets/custom-slider.liquid as [ { "<option1><option2>...": id }, ... ].
  // No map on the page = nothing to do, which is what keeps this inert on
  // every other template. Every failure path fails OPEN: a dead button on a
  // buyable variant is a certain lost sale, the bug it guards is a rare wrong one.
  // Liquid caps product.variants at 250, so a 294-variant product has a hole
  // in the map; the reverse lookup covers the common case where the STALE id
  // is mapped.
  // =====================================================================
  var GUARD_ATTR = 'data-cs-variant-guard';
  var GUARD_TITLE = 'Reloading options — reselect a colour to continue';
  var combos;            // undefined = not looked for yet, null = not on this page
  var guardTimer = null;

  function combinations() {
    if (combos !== undefined) return combos;
    combos = null;
    var el = document.getElementById('cb_combinations_custom_slider');
    if (el) {
      try { combos = JSON.parse(el.textContent); } catch (e) { combos = null; }
    }
    return combos;
  }

  // What the picker says the shopper chose: the native picker, which the
  // custom slider mirrors on every sync. Document order = the map's key order.
  function pickedKey() {
    var root = document.querySelector('.product__block__variants .form__selectors');
    if (!root) return null;
    var picked = root.querySelectorAll('input[type="radio"]:checked, input[data-popout-input]');
    if (!picked.length) return null;
    var key = '';
    for (var i = 0; i < picked.length; i++) key += picked[i].value;
    return key;
  }

  function idForKey(key) {
    var map = combinations();
    if (!map || !map.length || !key) return null;
    for (var j = 0; j < map.length; j++) {
      if (map[j][key] !== undefined) return String(map[j][key]);
    }
    return null;
  }

  function keyForId(id) {
    var map = combinations();
    if (!map || !map.length || !id) return null;
    for (var j = 0; j < map.length; j++) {
      for (var k in map[j]) {
        if (Object.prototype.hasOwnProperty.call(map[j], k) && String(map[j][k]) === String(id)) return k;
      }
    }
    return null;
  }

  function guardCheck() {
    if (!combinations()) return;                                   // not a slider PDP
    var add = document.querySelector('.product__main__content [name="add"]');
    if (!add || !add.form) return;
    var idInput = add.form.querySelector('[name="id"]');
    var formId = idInput && idInput.value;
    var picked = pickedKey();
    var mismatch = false;

    if (picked && formId) {
      var expected = idForKey(picked);
      if (expected) {
        mismatch = expected !== formId;
      } else {
        var formKey = keyForId(formId);                            // picked combo past the 250 cap
        mismatch = !!(formKey && formKey !== picked);
      }
    }

    if (mismatch) {
      if (!add.hasAttribute(GUARD_ATTR)) {
        // Record whether the disable is OURS, so the clear branch never
        // re-enables a button the server disabled (genuinely sold out).
        add.setAttribute(GUARD_ATTR, add.disabled ? 'flagged' : 'owned');
        add.disabled = true;
        add.setAttribute('title', GUARD_TITLE);
      }
    } else if (add.hasAttribute(GUARD_ATTR)) {
      var owned = add.getAttribute(GUARD_ATTR) === 'owned';
      add.removeAttribute(GUARD_ATTR);
      add.removeAttribute('title');
      if (owned) add.disabled = false;
    }
  }

  // A swap morphs the buy box twice (cache, then network ~470 ms later), and
  // theme.js announces each on `window` (not `document`). Check once the
  // updates stop, never mid-swap, when form and picker legitimately disagree.
  if (document.getElementById('cb_combinations_custom_slider')) {
    window.addEventListener('theme:section:update', function () {
      clearTimeout(guardTimer);
      guardTimer = setTimeout(guardCheck, 300);
    });
  }

  // =====================================================================
  // 2. Variant change: the original file's two jobs. Toggle the legacy
  // Klaviyo trigger, and on Colour Block product pages point each sibling
  // product's link at the same colour combination (?variant=…).
  // Markup from snippets/product-siblings-cb.liquid. Vanilla JS: jQuery is
  // only loaded on some product templates.
  // =====================================================================
  document.addEventListener('theme:variant:change', function (event) {
    var variant = event.detail && event.detail.variant;
    if (!variant) return;

    // From the original file: Klaviyo's LEGACY back-in-stock trigger, shown on
    // sold-out variants and hidden otherwise. Harmless when the element isn't
    // on the page (the current Klaviyo Forms button has no stable class).
    var legacyBis = document.querySelector('.btn.klaviyo-bis-trigger');
    if (legacyBis) legacyBis.style.display = variant.available === true ? 'none' : 'inline-block';

    if (!document.querySelector('.cb-product__block__siblings')) return;

    document.querySelectorAll('.cb-siblings-metadata.cross-sib').forEach(function (jsonEl) {
      var combosJson;
      try { combosJson = JSON.parse(jsonEl.textContent); } catch (e) { return; }
      for (var i = 0; i < combosJson.length; i++) {
        var c = combosJson[i];
        if (c.option1 !== variant.option1 || c.option2 !== variant.option2) continue;
        var links = document.querySelectorAll('.cb-product__block__siblings a[data-sib_prd_id="' + c.product_id + '"]');
        if (links.length) {
          var base = (links[0].getAttribute('href') || '').split('?')[0];
          links.forEach(function (link) { link.setAttribute('href', base + '?variant=' + c.id); });
        }
        break;
      }
    });
  });

  // =====================================================================
  // 3. Type-to-jump in the country and language dropdowns.
  //
  // The theme's <popout-select> is a list of links, not a native <select>, so
  // it lost the browser's own type-ahead. With the list open, typing marks the
  // first option that starts with what was typed ("b" -> Bahamas, "bulg" ->
  // Bulgaria), scrolls it to the top and highlights the typed letters. Enter
  // picks the marked option through the link's own click handler. The same
  // letter again cycles, a pause starts afresh, accents are ignored ("cote"
  // finds Côte d'Ivoire). It marks and never moves focus (the theme closes the
  // list when focus leaves it). Listens on document because Safari doesn't
  // focus a clicked button, so keys land on <body>; delegation also survives
  // header/footer re-renders in the editor. Styles: custom.css
  // (.ts-popout-active, .ts-popout-typed).
  // =====================================================================
  var TA_SCOPE = 'form[id^="localization-form"]';
  var TA_RESET_MS = 1000;
  var taQuery = '';
  var taTimer;
  var taMarked = null;   // { link, span, text } so the label can be put back

  function taFold(text) {
    return text.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
  }

  function taLabel(link) {
    return link.querySelector('span') || link;
  }

  // How many characters of text the query covers, or 0 if text doesn't start
  // with it. One character at a time, so "cote" covers the four letters of "Côte".
  function taCovered(text, query) {
    var folded = '', n = 0;
    for (var ch of text) {
      if (folded.length >= query.length) break;
      folded += taFold(ch);
      n += ch.length;
    }
    return folded.indexOf(query) === 0 ? n : 0;
  }

  function taClear() {
    if (!taMarked) return;
    taMarked.link.classList.remove('ts-popout-active');
    taMarked.span.textContent = taMarked.text;
    taMarked = null;
  }

  function taMark(link, covered) {
    taClear();
    var span = taLabel(link);
    var text = span.textContent;
    var lead = text.length - text.trimStart().length;
    var typed = document.createElement('mark');
    typed.className = 'ts-popout-typed';
    typed.textContent = text.slice(lead, lead + covered);
    span.replaceChildren(text.slice(0, lead), typed, text.slice(lead + covered));
    link.classList.add('ts-popout-active');
    taMarked = { link: link, span: span, text: text };
    var li = link.parentElement;                 // scroll the list only, never the page
    li.parentElement.scrollTop = li.offsetTop;
  }

  document.addEventListener('keydown', function (evt) {
    if (evt.ctrlKey || evt.metaKey || evt.altKey) return;
    if (evt.target.closest('input, textarea, select, [contenteditable]')) return;
    var list = document.querySelector(TA_SCOPE + ' .popout-list--visible');
    if (!list) return;
    var marked = taMarked && list.contains(taMarked.link) ? taMarked.link : null;

    if (evt.key === 'Enter') {
      // Not when an option has real focus (Tab): Enter picks that one natively.
      if (marked && !evt.target.closest('[data-popout-option]')) {
        evt.preventDefault();                   // stops the toggle's own Enter closing the list
        marked.click();
      }
      return;
    }
    if (evt.key.length !== 1) return;
    if (evt.key === ' ' && !taQuery) return;   // a leading Space keeps its normal job

    evt.preventDefault();
    clearTimeout(taTimer);
    taTimer = setTimeout(function () { taQuery = ''; }, TA_RESET_MS);
    taQuery += taFold(evt.key);

    var options = Array.prototype.slice.call(list.querySelectorAll('[data-popout-option]'));
    var cycling = taQuery.split('').every(function (c) { return c === taQuery[0]; });
    var needle = cycling ? taQuery[0] : taQuery;
    var start = cycling ? options.indexOf(marked) + 1 : 0;
    for (var i = 0; i < options.length; i++) {
      var option = options[(start + i) % options.length];
      var covered = taCovered(taLabel(option).textContent.trimStart(), needle);
      if (covered) {
        taMark(option, covered);
        return;
      }
    }
  });

  // Opening or closing the list starts afresh.
  document.addEventListener('click', function (evt) {
    if (!evt.target.closest(TA_SCOPE + ' [data-popout-toggle]')) return;
    taClear();
    taQuery = '';
  });

  // =====================================================================
  // 4. PDP countdown (snippets/cb-pdp-countdown-timer.liquid).
  // =====================================================================
  var countdownEl = document.getElementById('cb-pdp-countdown');
  if (countdownEl) {
    var deadline = new Date(countdownEl.dataset.deadline).getTime();
    var days = document.getElementById('days');
    var hours = document.getElementById('hours');
    var mins = document.getElementById('mins');
    var secs = document.getElementById('secs');
    var pad = function (n) { return String(n).padStart(2, '0'); };
    var countdownTimer = null;
    var tick = function () {
      var diff = deadline - Date.now();
      if (diff <= 0) {
        days.textContent = hours.textContent = mins.textContent = secs.textContent = '00';
        clearInterval(countdownTimer);
        var wrapper = countdownEl.closest('.cb-pdp-countdown-wrapper');
        if (wrapper) wrapper.remove();
        return;
      }
      var t = Math.floor(diff / 1000);
      days.textContent = pad(Math.floor(t / 86400));
      hours.textContent = pad(Math.floor((t % 86400) / 3600));
      mins.textContent = pad(Math.floor((t % 3600) / 60));
      secs.textContent = pad(t % 60);
    };
    countdownTimer = setInterval(tick, 1000);
    tick();
  }
})();
// =====================================================================
// 5. Intelligems test price for the Add to Cart button.
//
// The button price is Alpine x-text built from Liquid-baked cents, so on a
// quantity change it would overwrite the test price Intelligems swapped into
// the tagged span. snippets/product-buttons.liquid asks this first. Returns
// cents; falls back to the theme's price when no test applies.
// =====================================================================
window.tsTestPrice = function (variantId, cents) {
  try {
    var p = window.igData && window.igData.price && window.igData.price.getPriceByVariantId(variantId);
    if (p === null || p === undefined) return cents;
    var s = String(p).replace(/[^0-9.,]/g, '');
    if (s.indexOf(',') > -1 && s.indexOf('.') === -1) s = s.replace(',', '.');
    var n = Number(s.replace(/,/g, ''));
    return isFinite(n) && n > 0 ? Math.round(n * 100) : cents;
  } catch (e) {
    return cents;
  }
};
